import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { PageChangedEvent } from './paginator.model';

@Component({
  selector: 'ds365-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  standalone: false
})
export class PaginatorComponent implements OnChanges, OnInit {
  @Output() pageChanged = new EventEmitter<PageChangedEvent>();

  @Input() defaultPageSize = 10;
  @Input() pageSizeOptions = [10];
  @Input() position = 'center';
  @Input() maxPageLink = 7;
  @Input() showFirstLast = true;
  @Input() viewAllSoftLimit = 1000;
  @Input() disabled = false;
  @Input() loading = false;

  count = 0;
  @Input('count')
  set countValue(value: number) {
    this.count = value || 0;
  }

  @Input('defaultCurrentPage')
  set defaultCurrentPage(value: number) {
    this._currentPage = value || 1;
  }

  _pages = 1;
  _pageSize: number;
  _currentPage = 1;
  _viewAllMode = false;

  ngOnInit() {
    this._pageSize = this._pageSize || this.defaultPageSize;
    setTimeout(() => {
      this.pageChanged.emit(new PageChangedEvent(this._currentPage, this._pageSize));
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['count'] || changes['defaultPageSize']) {
      if (changes['defaultPageSize']) {
        this._pageSize = this.defaultPageSize;
      } else {
        this._pageSize = this._pageSize || this.defaultPageSize;
      }
      this._pages = Math.max(1, Math.ceil((this.count || 0) / Math.max(1, this._pageSize)));
      if (this._currentPage > this._pages && !this._viewAllMode) {
        this._currentPage = this._pages;
      }
    }
  }


  get pageCount(): number { return this._viewAllMode ? 1 : Math.max(1, Math.ceil(this.count / Math.max(1, this._pageSize))); }

  get startIndex(): number {
    if (!this.count) return 0;
    return this._viewAllMode ? 1 : ((this._currentPage - 1) * this._pageSize) + 1;
  }

  get endIndex(): number {
    if (!this.count) return 0;
    if (this._viewAllMode) return this.count;
    return Math.min(this._currentPage * this._pageSize, this.count);
  }

  get rangeLabel(): string {
    return `${this.startIndex}–${this.endIndex} of ${this.count}`;
  }

  get pageWindow(): (number | '…')[] {
    const total = this.pageCount;
    const maxBtns = (this.maxPageLink && this.maxPageLink > 0) ? this.maxPageLink : 7;

    if (this._viewAllMode) return [];
    if (total <= maxBtns) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const current = this._currentPage;
    const inner = Math.max(3, maxBtns - 2);
    let start = Math.max(2, current - Math.floor((inner - 1) / 2));
    let end = Math.min(total - 1, start + inner - 1);
    if (end - start + 1 < inner) start = Math.max(2, end - inner + 1);

    const out: (number | '…')[] = [1];
    if (start > 2) out.push('…');
    for (let p = start; p <= end; p++) out.push(p);
    if (end < total - 1) out.push('…');
    out.push(total);
    return out;
  }

  changePageSize(pageSize: number) {
    if (this.disabled || this.loading) return;
    this._viewAllMode = false;
    this._pageSize = Math.max(1, pageSize);
    this._pages = Math.max(1, Math.ceil(this.count / this._pageSize));
    this.goToPage(1);
  }

  goToPage(page: number) {
    if (this.disabled || this.loading || this._viewAllMode) return;
    const clamped = Math.min(Math.max(1, page), this.pageCount);
    if (this._currentPage !== clamped) {
      this._currentPage = clamped;
      this.pageChanged.emit(new PageChangedEvent(this._currentPage, this._pageSize));
    }
  }

  first() { if (this._currentPage > 1) this.goToPage(1); }
  prev() { if (this._currentPage > 1) this.goToPage(this._currentPage - 1); }
  next() { if (this._currentPage < this.pageCount) this.goToPage(this._currentPage + 1); }
  last() { if (this._currentPage < this.pageCount) this.goToPage(this.pageCount); }

  viewAll() {
    if (this.disabled || this.loading) return;
    if (this.count > this.viewAllSoftLimit) return;
    this._viewAllMode = true;
    this.pageChanged.emit(new PageChangedEvent(0, 0));
  }

  reset() {
    this._viewAllMode = false;
    this._currentPage = 1;
    this._pageSize = this.defaultPageSize;
    this._pages = Math.max(1, Math.ceil(this.count / this._pageSize));
    this.pageChanged.emit(new PageChangedEvent(this._currentPage, this._pageSize));
  }

  getClassPosition() {
    return `justify-content-${this.position}`;
  }
  resetPages(){
    this._viewAllMode = false;
    this._currentPage = 1;
    this._pageSize = this.defaultPageSize;
  }
  
}
